import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Gift, ArrowRight, Check, TrendingUp, Shield, 
  BarChart3, Bell, Zap, Crown, Sparkles
} from 'lucide-react';
import SEO from '../components/SEO';

const ReferralLandingPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [referrerName, setReferrerName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    validateCode();
  }, [code]);

  const validateCode = async () => {
    try {
      const response = await axios.get(`/referral/validate/${code}`);
      if (response.data.valid) {
        setValid(true);
        setReferrerName(response.data.referrer_name);
      }
    } catch (error) {
      console.error('Error validating code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = () => {
    navigate(`/register?ref=${code}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title={`${referrerName || 'Bir arkadaşın'} seni Borsamex'e davet etti! | %15 İndirim`}
        description="Borsamex'e katıl, AI destekli kripto sinyalleri al. İlk ödemende %15 indirim kazan!"
      />

      {/* Hero */}
      <section className="relative overflow-hidden py-16 md:py-24">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-primary/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-20 right-1/4 w-72 h-72 bg-amber-500/20 rounded-full blur-[100px]" />
        
        <div className="container mx-auto px-6 relative">
          <div className="text-center max-w-2xl mx-auto">
            
            {/* Referrer Badge */}
            {valid && referrerName && (
              <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/50 rounded-full px-4 py-2 mb-6">
                <Gift className="h-4 w-4 text-primary" />
                <span className="text-primary font-medium">
                  {referrerName} seni davet etti!
                </span>
              </div>
            )}

            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Kripto Sinyallerinde
              <br />
              <span className="text-gradient">%15 İndirimle Başla!</span>
            </h1>
            
            <p className="text-lg text-slate-400 mb-8">
              AI destekli teknik analiz sinyalleri ile kripto piyasasında avantaj yakala.
              {valid && (
                <span className="block mt-2 text-amber-400">
                  🎁 Bu özel linkle ilk ödemende %15 indirim senin!
                </span>
              )}
            </p>

            {/* Discount Badge */}
            {valid && (
              <div className="flex justify-center mb-8">
                <div className="bg-gradient-to-r from-primary/20 to-amber-500/20 border border-primary/50 rounded-2xl p-6">
                  <p className="text-sm text-slate-400 mb-1">Referans Kodu</p>
                  <p className="text-3xl font-mono font-bold text-primary mb-2">{code}</p>
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50">
                    %15 İNDİRİM
                  </Badge>
                </div>
              </div>
            )}

            <Button 
              size="lg" 
              onClick={handleRegister}
              className="bg-primary hover:bg-primary/90 text-lg px-8 py-6"
            >
              <Sparkles className="h-5 w-5 mr-2" />
              Hemen Kayıt Ol ve Başla
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>

            <p className="text-sm text-slate-500 mt-4">
              Ücretsiz başla, istediğin zaman Pro&apos;ya geç
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-bold text-center mb-12">Borsamex ile Neler Kazanırsın?</h2>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">AI Destekli Sinyaller</h3>
              <p className="text-sm text-slate-400">
                Gelişmiş algoritmalar ile oluşturulan AL/SAT sinyalleri
              </p>
            </div>
            
            <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
                <Bell className="h-6 w-6 text-amber-400" />
              </div>
              <h3 className="font-semibold mb-2">Anlık Bildirimler</h3>
              <p className="text-sm text-slate-400">
                Telegram ve e-posta ile anında sinyal bildirimleri
              </p>
            </div>
            
            <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-blue-400" />
              </div>
              <h3 className="font-semibold mb-2">Performans Takibi</h3>
              <p className="text-sm text-slate-400">
                Tüm sinyallerin detaylı performans geçmişi
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Güvenle Kullan</h2>
            <p className="text-slate-400 mb-8">
              Borsamex yatırım tavsiyesi vermez. Sunulan sinyaller, teknik analize dayalı 
              fikir paylaşımıdır. Yatırım kararları tamamen size aittir.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Check className="h-4 w-4 text-green-400" />
                <span>15+ Kripto Para</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Check className="h-4 w-4 text-green-400" />
                <span>7/24 Piyasa Takibi</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Check className="h-4 w-4 text-green-400" />
                <span>Detaylı Analizler</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-t from-primary/10 to-transparent">
        <div className="container mx-auto px-6 text-center">
          <Crown className="h-12 w-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">
            {valid ? '%15 İndirimini Kaçırma!' : 'Hemen Başla!'}
          </h2>
          <p className="text-slate-400 mb-8 max-w-lg mx-auto">
            {valid 
              ? `${referrerName || 'Arkadaşın'} sayesinde ilk Pro ödemende %15 indirim kazanıyorsun.`
              : 'Borsamex ile kripto piyasasında bir adım önde ol.'
            }
          </p>
          
          <Button 
            size="lg" 
            onClick={handleRegister}
            className="bg-primary hover:bg-primary/90 text-lg px-8"
          >
            Kayıt Ol
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ReferralLandingPage;
